import React, { useState } from "react";
import { StepShell, Field, Chips, YesNo, SelectNative, TInput } from "./WizardUI";
import { AUTOMATIONS, EXCLUDE_TYPES, TIMEZONES } from "./constants";

const PLATFORM_OPTS = ["facebook", "instagram", "x", "tiktok", "youtube", "threads"];

export default function Step9Automation({ saved, station, onSave, onBack, onSkip, busy }) {
  const p = { ...((station || {}).automation_prefs || {}), ...(saved || {}) };
  const [f, setF] = useState({
    now_playing_posts: p.now_playing_posts ?? false, artist_info: p.artist_info ?? false,
    wordpress_posts: p.wordpress_posts ?? false, podcast_creation: p.podcast_creation ?? false,
    social_scheduling: p.social_scheduling ?? false, news_generation: p.news_generation ?? false,
    weather_reports: p.weather_reports ?? false, show_notes: p.show_notes ?? false,
    promo_posts: p.promo_posts ?? false,
    platforms: p.platforms || ["facebook", "instagram"],
    frequency: p.frequency || "daily",
    hour: p.hour ?? 9, minute: p.minute ?? 0,
    timezone: p.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    approval_required: p.approval_required ?? true,
    dedupe_hours: p.dedupe_hours ?? 24,
    excluded_types: p.excluded_types || ["Station IDs", "Sweepers", "Commercials", "Jingles", "Unknown metadata", "Blank metadata"],
    quiet_start: p.quiet_start || "23:00", quiet_end: p.quiet_end || "07:00",
    max_daily_posts: p.max_daily_posts ?? 10,
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const anyOn = AUTOMATIONS.some((a) => f[a.k]);

  return (
    <StepShell step={9} title="Automation settings"
      subtitle="Turn on the workflows you want running hands-free. Everything respects your approval settings."
      onBack={onBack} onContinue={() => onSave(f)} onSkip={onSkip} busy={busy}>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {AUTOMATIONS.map((a) => (
          <YesNo key={a.k} label={a.l} value={f[a.k]} onChange={set(a.k)} testid={`onb-auto-${a.k}`} />
        ))}
      </div>
      {anyOn && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-4" data-testid="onb-auto-config">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// Configure your automations</div>
          <Field label="Platforms to post to">
            <Chips options={PLATFORM_OPTS} values={f.platforms} onChange={set("platforms")} testidPrefix="onb-auto-plat" />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Frequency">
              <SelectNative options={[{ v: "daily", l: "Daily" }, { v: "weekdays", l: "Weekdays" }, { v: "weekly", l: "Weekly" }, { v: "per_song", l: "Every song" }]}
                value={f.frequency} onChange={set("frequency")} testid="onb-auto-freq" />
            </Field>
            <Field label="Posting time">
              <div className="flex items-center gap-1">
                <TInput type="number" min={0} max={23} value={f.hour} onChange={(e) => set("hour")(Number(e.target.value))} />
                <span className="text-ink-muted">:</span>
                <TInput type="number" min={0} max={59} value={f.minute} onChange={(e) => set("minute")(Number(e.target.value))} />
              </div>
            </Field>
            <Field label="Time zone">
              <SelectNative options={TIMEZONES} value={f.timezone} onChange={set("timezone")} />
            </Field>
            <Field label="Max daily posts">
              <TInput type="number" min={1} max={100} value={f.max_daily_posts}
                onChange={(e) => set("max_daily_posts")(Number(e.target.value))} data-testid="onb-auto-max" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <YesNo label="Approval required before posting" value={f.approval_required} onChange={set("approval_required")} testid="onb-auto-approval" />
            <Field label="Duplicate-post prevention (hours)">
              <TInput type="number" min={0} max={168} value={f.dedupe_hours}
                onChange={(e) => set("dedupe_hours")(Number(e.target.value))} />
            </Field>
            <Field label="Quiet hours">
              <div className="flex items-center gap-1">
                <TInput type="time" value={f.quiet_start} onChange={(e) => set("quiet_start")(e.target.value)} />
                <span className="text-ink-muted text-xs">to</span>
                <TInput type="time" value={f.quiet_end} onChange={(e) => set("quiet_end")(e.target.value)} />
              </div>
            </Field>
          </div>
          <Field label="Never post these" hint="Metadata matching these types is skipped automatically.">
            <Chips options={EXCLUDE_TYPES} values={f.excluded_types} onChange={set("excluded_types")} testidPrefix="onb-auto-excl" />
          </Field>
        </div>
      )}
    </StepShell>
  );
}
